import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import UserAvatar from "../components/UserAvatar";
import { toast } from "sonner";
import {
  ArrowLeft, Search, Loader2, Star, Download, ShoppingBag,
  FileText, Heart, Palette, MessageCircle, BookOpen, Sparkles,
  Home, Plus, Bell, User
} from "lucide-react";
import api from "../lib/api";

const CATEGORIES = [
  { id: "", label: "All" },
  { id: "productivity", label: "Productivity" },
  { id: "creative", label: "Creative" },
  { id: "business", label: "Business" },
  { id: "education", label: "Education" },
  { id: "entertainment", label: "Entertainment" },
  { id: "lifestyle", label: "Lifestyle" },
  { id: "health", label: "Health" },
  { id: "technology", label: "Technology" },
];

const TYPE_ICONS = {
  ai_template: FileText, content_pack: BookOpen, ecard_pack: Heart,
  prompt_pack: Sparkles, guide: BookOpen, custom: Palette,
};

const SORT_OPTIONS = [
  { id: "newest", label: "Newest" },
  { id: "popular", label: "Trending" },
  { id: "top_rated", label: "Top Rated" },
  { id: "price_low", label: "Price: Low" },
  { id: "price_high", label: "Price: High" },
];

export default function MarketplacePage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    api.get("/profile/me").then((r) => setProfile(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ sort: sortBy, page: 1, limit: 30 });
    if (category) params.set("category", category);
    if (searchQuery) params.set("q", searchQuery);
    api.get(`/marketplace/items?${params}`)
      .then((r) => setItems(r.data.items))
      .catch(() => toast.error("Failed to load marketplace"))
      .finally(() => setLoading(false));
  }, [category, sortBy, searchQuery]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchQuery(searchInput.trim());
  };

  return (
    <div className="min-h-screen pb-20 lg:pb-0" data-testid="marketplace-page">
      {/* Top Bar */}
      <div className="sticky top-0 z-30 nav-glass px-4">
        <div className="max-w-5xl mx-auto flex items-center gap-3 h-12">
          <button onClick={() => navigate("/feed")} className="text-muted-foreground hover:text-foreground/80 bg-transparent border-0 cursor-pointer"><ArrowLeft className="w-5 h-5" /></button>
          <ShoppingBag className="w-4 h-4 text-cyan-400" />
          <h1 className="font-outfit text-base font-semibold text-foreground mr-4">Marketplace</h1>
          <form onSubmit={handleSearch} className="flex-1 relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/70" />
            <input data-testid="marketplace-search" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Search content..." className="w-full pl-9 pr-4 py-1.5 bg-muted/40 border border-border rounded-full text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-cyan-400/25" />
          </form>
          <Link to="/marketplace/create" className="text-xs font-medium text-cyan-400 no-underline hover:text-cyan-300 whitespace-nowrap" data-testid="create-listing-link">+ Create Listing</Link>
          <Link to="/creators" className="text-xs font-medium text-purple-400 no-underline hover:text-purple-300 whitespace-nowrap" data-testid="browse-creators-link">Browse Creators</Link>
        </div>
        {/* Category pills */}
        <div className="max-w-5xl mx-auto flex gap-1.5 pb-2 overflow-x-auto">
          {CATEGORIES.map((c) => (
            <button key={c.id} onClick={() => setCategory(c.id)} className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap border-0 cursor-pointer transition-all ${category === c.id ? "bg-cyan-500/10 text-cyan-400" : "bg-transparent text-muted-foreground hover:bg-white/5"}`} data-testid={`cat-${c.id || "all"}`}>
              {c.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-4">
        {/* Banner */}
        <div className="rounded-xl overflow-hidden h-32 relative mb-4" data-testid="marketplace-banner">
          <img src="https://static.prod-images.emergentagent.com/jobs/69522626-12bf-4c16-9d63-ce7a21ed458c/images/1aff30fc723f19d7eb3d9835e74a780be0cbe97d865d42c526e8a9690904cf7e.jpeg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-indigo-900/70 to-purple-900/40" />
          <div className="absolute inset-0 flex items-center px-6">
            <div>
              <h2 className="font-outfit text-xl font-semibold text-white drop-shadow-lg">Content Marketplace</h2>
              <p className="text-xs text-cyan-200 mt-1">Discover AI-powered templates, guides, and more</p>
            </div>
          </div>
        </div>
        {/* Sort */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-xs text-muted-foreground">{items.length} items</p>
          <div className="flex gap-1">
            {SORT_OPTIONS.map((s) => (
              <button key={s.id} onClick={() => setSortBy(s.id)} className={`px-2.5 py-1 rounded text-[11px] font-medium border-0 cursor-pointer ${sortBy === s.id ? "bg-cyan-500/10 text-cyan-400" : "bg-transparent text-muted-foreground/70 hover:text-muted-foreground"}`} data-testid={`sort-${s.id}`}>
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-cyan-400 animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-16"><ShoppingBag className="w-8 h-8 text-muted-foreground/60 mx-auto mb-3" /><p className="text-sm text-muted-foreground">No items found</p></div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="marketplace-grid">
            {items.map((item) => {
              const TypeIcon = TYPE_ICONS[item.content_type] || FileText;
              return (
                <Link to={`/marketplace/${item.id}`} key={item.id} className={`bg-card border border-border/60 rounded-xl overflow-hidden no-underline card-hover group cat-accent-${item.category || "productivity"} ${item.downloads_count > 30 ? "glow-popular" : ""}`} data-testid={`item-card-${item.id}`}>
                  {/* Preview Image */}
                  <div className="h-32 bg-gradient-to-br from-cyan-500/10 to-violet-500/10 flex items-center justify-center">
                    <TypeIcon className="w-10 h-10 text-cyan-300 group-hover:text-cyan-400 transition-colors" />
                  </div>
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-1.5">
                      <h3 className="text-sm font-semibold text-foreground line-clamp-1 flex-1">{item.title}</h3>
                      <span className={`text-xs font-semibold ml-2 px-2 py-0.5 rounded-full ${item.is_free ? "bg-emerald-500/10 text-emerald-400" : "bg-cyan-500/10 text-cyan-400"}`}>
                        {item.is_free ? "Free" : `$${Number(item.price ?? 0).toFixed(2)}`}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-2 mb-3">{item.description}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <UserAvatar name={item.seller_display_name} username={item.seller_username} size="xs" />
                        <Link to={`/creators/${item.seller_username}`} onClick={(e) => e.stopPropagation()} className="text-[10px] text-muted-foreground/70 no-underline hover:text-cyan-400">{item.seller_display_name}</Link>
                      </div>
                      <div className="flex items-center gap-2 text-[10px] text-muted-foreground/70">
                        {item.rating_count > 0 && (
                          <span className="flex items-center gap-0.5"><Star className="w-3 h-3 text-amber-400 fill-amber-400" /> {item.rating_avg}</span>
                        )}
                        <span className="flex items-center gap-0.5"><Download className="w-3 h-3" /> {item.downloads_count}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* Mobile Bottom Tabs */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-card border-t border-border/60 h-14 flex items-center justify-around px-2">
        <Link to="/feed" className="flex flex-col items-center gap-0.5 no-underline text-muted-foreground/70"><Home className="w-5 h-5" /><span className="text-[10px]">Home</span></Link>
        <Link to="/marketplace" className="flex flex-col items-center gap-0.5 no-underline text-cyan-400"><ShoppingBag className="w-5 h-5" /><span className="text-[10px]">Market</span></Link>
        <Link to="/feed" className="flex flex-col items-center gap-0.5 no-underline text-muted-foreground/70"><Plus className="w-5 h-5" /></Link>
        <Link to="/notifications" className="flex flex-col items-center gap-0.5 no-underline text-muted-foreground/70"><Bell className="w-5 h-5" /><span className="text-[10px]">Notifs</span></Link>
        <Link to={profile ? `/profile/${profile.username}` : "/feed"} className="flex flex-col items-center gap-0.5 no-underline text-muted-foreground/70"><User className="w-5 h-5" /><span className="text-[10px]">Profile</span></Link>
      </div>
    </div>
  );
}
